import { type AdvizeoModalComponent, injectAdvizeoModalInput } from '@/components/advizeo-modal-spawner/advizeo-modal-spawner.component';
import { ErrorHandlingProvider } from '@/providers/error-handling/error-handling.provider';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { UiFormErrorComponent } from '@/ui/form-field/form-error/ui-form-error';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { trackControlValidity } from '@/utils/angular/forms/reactive-forms/reactive-forms-signals-interop.utils';
import { translate } from '@/utils/i18n/translate.reactive-helpers';
import { Component, computed, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import type { AlertScenarioId } from '../../alerts.interface';
import { WaterLeakageScenarioService } from '../water-leakage-scenario-service/water-leakage-scenario.service';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./water-leakage-scenario-creation-modal.scss'],
    selector: 'water-leakage-scenario-creation-modal',
    templateUrl: 'water-leakage-scenario-creation-modal.html',
    imports: [FormsModule, ReactiveFormsModule, UiFormFieldComponent, AdvizeoInputDirective, UiFormErrorComponent, TranslatePipe]
})
export class WaterLeakageScenarioCreationModalComponent implements AdvizeoModalComponent<null, AlertScenarioId | null> {
    private _translateService = inject(TranslateService);
    private _fb = inject(NonNullableFormBuilder);
    private _waterLeakageScenarioService = inject(WaterLeakageScenarioService);
    private _errorHandlingProvider = inject(ErrorHandlingProvider);

    title = translate('PAGES.ALERT_SCENARIO.CREATE_WATER_LEAKAGE_SCENARIO');
    isDismissable = signal(true);

    input = injectAdvizeoModalInput<null>();
    close = output<AlertScenarioId | void>();

    form = this._fb.group({
        name: ['', Validators.required]
    });

    isFormValid = trackControlValidity(this.form);

    buttons = computed(() => [
        {
            text: this._translateService.instant('BUTTONS.VALIDATE'),
            onClick: () => {
                this.createScenario();
            },
            isDisabled: !this.isFormValid(),
            color: SharedUiColorNameV2.PRIMARY
        }
    ]);

    async createScenario() {
        const formValue = this.form.getRawValue();
        try {
            const alertScenarioId = await this._waterLeakageScenarioService.createWaterLeakageScenario({
                name: formValue.name
            });
            this.close.emit(alertScenarioId);
        } catch (err) {
            const errors = this._waterLeakageScenarioService.extractErrorsFromApiFailure(
                this._errorHandlingProvider.getErrorCodeFromApiResponse(err),
                this._errorHandlingProvider.getErrorPayloadFromApiResponse(err)
            );
            this._waterLeakageScenarioService.showErrors(errors);
        }
    }
}
